import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Drawer } from '@/shared/ui/Drawer';
import { LoadingState, UnavailableState } from '@/shared/ui/States';

import { usePermissionCatalogue } from '../api/access';

import type { Role } from '@/shared/api/generated/tenancy';

/* What a role actually grants, grouped by the resource type it governs.
 *
 * **Grouped, never a flat list.** The catalogue is over a hundred permissions across thirty-five
 * resource types, and nobody reads that as a list. An administrator deciding whether a role is too
 * wide is asking "what may this do to *rules*", and the resource type is that question's axis — so
 * the API returns it already grouped and this renders the grouping rather than re-deriving one.
 *
 * **Read-only, and it says why.** No route creates or edits a role. That is deliberate rather than
 * unfinished: `DecisionCache` invalidates when a role is *unbound* from somebody, not when a role's
 * *contents* change, so an edit here would take effect on a timer with no way to tell a reader
 * when. An absent edit control with no explanation reads as an unfinished screen, so the reason is
 * on the panel.
 *
 * **The permission string is `action:resource_type`.** That is the order `authz.role.permissions`
 * stores and the API sends — the kernel's `Permission.key` renders the reverse for logs, and its own
 * docstring warns that writing roles from that property produces permissions which parse back
 * swapped and grant silently nothing.
 */

export interface RoleMatrixProps {
  readonly roleName: string;
  readonly roles: readonly Role[];
  readonly onClose: () => void;
}

export function RoleMatrix({ roleName, roles, onClose }: RoleMatrixProps): ReactElement {
  const { t } = useTranslation();
  const catalogue = usePermissionCatalogue();
  const role = roles.find((candidate) => candidate.name === roleName);

  /* A set, because the lookup below runs once per action in the catalogue — over a hundred of them
   * against a role holding up to a hundred and eleven, which as nested `includes` is quadratic on
   * every render of an open drawer. */
  const held = new Set(role?.permissions ?? []);

  return (
    <Drawer
      title={t('admin.matrix.title', { role: roleName })}
      closeLabel={t('admin.drawer.close')}
      onClose={onClose}
      meta={
        <span className="lq-muted">
          {t('admin.matrix.count', { count: role?.permissions.length ?? 0 })}
        </span>
      }
    >
      <p className="lq-form-note">{t('admin.matrix.readOnly')}</p>

      {catalogue.isPending ? (
        <LoadingState rows={4} label={t('admin.matrix.loading')} />
      ) : catalogue.isError ? (
        <UnavailableState
          title={t('admin.matrix.unreadable')}
          guidance={catalogue.error.message}
        />
      ) : (
        <div className="lq-stack">
          {catalogue.data.items.map((group) => {
            const granted = group.actions.filter((action) =>
              held.has(`${action}:${group.resource_type}`),
            );
            return (
              <section key={group.resource_type} className="lq-matrix-group">
                <h4 className="lq-matrix-group__name">
                  <span className="lq-mono">{group.resource_type}</span>
                  <span className="lq-muted">
                    {' '}
                    {t('admin.matrix.groupCount', {
                      granted: granted.length,
                      total: group.actions.length,
                    })}
                  </span>
                </h4>
                <ul className="lq-matrix-actions">
                  {group.actions.map((action) => {
                    const has = held.has(`${action}:${group.resource_type}`);
                    return (
                      <li
                        key={action}
                        className={
                          has
                            ? 'lq-matrix-action lq-matrix-action--granted'
                            : 'lq-matrix-action'
                        }
                      >
                        {/* A mark *and* a class, so the grant survives greyscale — the same rule
                            every status in this kit follows. The mark is aria-hidden because the
                            list item's own class carries nothing to a screen reader; the text
                            beside it does the announcing. */}
                        <span aria-hidden="true" className="lq-matrix-action__mark">
                          {has ? '✓' : '·'}
                        </span>
                        <span className="lq-mono">{action}</span>
                        <span className="lq-visually-hidden">
                          {has ? t('admin.matrix.granted') : t('admin.matrix.notGranted')}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </Drawer>
  );
}
